import AutorenewIcon from '@mui/icons-material/Autorenew';
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import { startCase } from "lodash";
import { useCallback } from 'react';
import * as Yup from "yup";
import { useMerchantTerminal } from "../../hooks/useMerchantTerminal";
import { handleOpenLink } from "../../utils/appUtils";
import {
  convertAmountByCurrency,
  noCentsCurrencies,
  validCurrencies,
} from "../../utils/constants";
import { generate_signature } from "../../utils/encryptionUtils";
import { randomStringByNumber } from '../../utils/string';
import { styles } from "../../utils/styles";
import { showErrorMsg } from "../../utils/toast";

const instance = axios.create();

const initialValues = {
  merchant_id: "",
  terminal_id: "",
  api_key: "",
  sign_key: "",
  merchant_reference_id: `${new Date().getTime()}`,
  description: "",
  currency: "",
  amount: "",
  name: "",
  email: "",
  phone: "",
  success_url: "",
  failure_url: "",
  notification_url: "",
  capture: false,
};

export default function TestPaymentPageForm() {
  const {
    handleChange,
    handleSubmit,
    handleBlur,
    values,
    touched,
    errors,
    isSubmitting,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema: Yup.object({
      merchant_id: Yup.object().required("The Merchant Name field is required."),
      terminal_id: Yup.string().required("The Terminal ID field is required."),
      api_key: Yup.string()
        .required("The API Key field is required.")
        .matches(
          /^ak-[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/,
          "Invalid API Key format"
        ),
      sign_key: Yup.string()
        .required("The Sign Key field is required.")
        .matches(
          /^sk-[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/,
          "Invalid Sign Key format"
        ),
      merchant_reference_id: Yup.string().required(
        "The Merchant Reference ID field is required."
      ),
      description: Yup.string().required("The Description field is required."),
      currency: Yup.string()
        .required("The Transaction Currency field is required.")
        .test(
          "valid-currency",
          "Invalid currency",
          (value) => value && validCurrencies.includes(value.toUpperCase())
        ),
      amount: Yup.number()
        .required("The Transaction Amount field is required.")
        .test("valid-amount", "Invalid amount", (value, context) => {
          const currency = context.parent.currency?.toUpperCase();
          if (noCentsCurrencies.includes(currency)) {
            return (
              Number.isInteger(value) &&
              value >= 1 &&
              "Amount must be an integer and at least 1 for selected currency"
            );
          }
          return (
            value >= 0.01 &&
            /^[0-9]\d*(\.\d{1,2})?$/.test(value) &&
            "Amount must be at least 0.01 and up to 2 decimal places"
          );
        }),
      name: Yup.string().required("The Payer Name field is required."),
      email: Yup.string()
        .required("The Payer Email field is required.")
        .email("Invalid email format"),
      phone: Yup.string().required("The Payer Phone field is required."),
      success_url: Yup.string().required("The Success URL field is required."),
      failure_url: Yup.string().required("The Failure URL field is required."),
      notification_url: Yup.string().required(
        "The Notification URL field is required."
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const {
        merchant_id,
        terminal_id,
        merchant_reference_id,
        description,
        success_url,
        failure_url,
        notification_url,
        name,
        email,
        phone,
        capture,
        api_key,
        sign_key,
      } = values;

      const headers = {
        Authorization: "Bearer " + api_key,
      };

      const currency = values.currency.toUpperCase();
      const body = {
        merchant_id: merchant_id.id,
        terminal_id,
        merchant_reference_id,
        description: description?.trim(),
        success_url: success_url?.trim(),
        failure_url: failure_url?.trim(),
        notification_url: notification_url?.trim(),
        amount: {
          value: convertAmountByCurrency(currency, values.amount),
          currency: currency,
        },
        source: {
          name: name?.trim(),
          email: email?.trim(),
          phone: phone?.trim(),
        },
        capture,
      };

      const url = process.env.REACT_APP_API_URL + "/payment-page";

      try {
        const { data } = await instance.post(url, body, {
          headers: {
            ...headers,
            Signature: generate_signature(body, sign_key),
          },
        });

        if (data.body && data.body["oxygen7_hpp_url"]) {
          const redirectUrl = data.body["oxygen7_hpp_url"];
          handleOpenLink(redirectUrl);
          resetForm({
            values: {
              ...initialValues,
              merchant_reference_id: `${new Date().getTime()}`,
            },
          });
        } else {
          showErrorMsg(data.msg || "No redirect url found. Please try again.");
        }
      } catch (error) {
        let msg = "";
        if (error?.response?.data?.body?.status_desc) {
          msg = error?.response?.data?.body?.status_desc;
        } else if (error?.response?.data?.message) {
          msg = error?.response?.data?.message;
        } else if (error?.message) {
          msg = error?.message;
        } else {
          msg = error?.toString();
        }

        showErrorMsg(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    merchants,
    terminals,
    isLoadedMerchants,
    isLoadedTerminals,
    selectedTerminal,
    onChangeMerchant,
  } = useMerchantTerminal(setValues);

  const handleRandom = useCallback(() => {
    const str = randomStringByNumber(13);
    setValues({
      ...values,
      merchant_reference_id: str,
    });
  }, [values, setValues]);

  const renderMerchantsOption = () => {
    if (merchants.length === 0) return []

    return merchants.map(item => ({
      id: item.mid,
      label: item.name
    }))
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, m: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Merchant Info */}
          <Typography variant="h6" gutterBottom>
            Merchant Info
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disablePortal
                loading={!isLoadedMerchants}
                id="merchant_id"
                name="merchant_id"
                size="small"
                value={values["merchant_id"]}
                onChange={onChangeMerchant}
                disabled={isSubmitting}
                options={renderMerchantsOption()}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={"Merchant Name"} 
                    onBlur={handleBlur}
                    error={touched["merchant_id"] && Boolean(errors["merchant_id"])}
                    helperText={touched["merchant_id"] && errors["merchant_id"]}
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disablePortal
                loading={!isLoadedTerminals}
                id="terminal_id"
                name="terminal_id"
                size="small"
                key={selectedTerminal}
                value={values["terminal_id"]}
                onChange={(e, newValue) => {
                  setFieldValue("terminal_id", newValue ? newValue : "");
                }}
                disabled={isSubmitting}
                options={terminals}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={startCase("terminal_id")}
                    onBlur={handleBlur}
                    error={touched["terminal_id"] && Boolean(errors["terminal_id"])}
                    helperText={touched["terminal_id"] && errors["terminal_id"]}
                    />
                )}
                fullWidth
              />
            </Grid>
            {["api_key", "sign_key"].map((id) => (
              <Grid item xs={12} md={6} key={id}>
                <TextField
                  fullWidth
                  size="small"
                  id={id}
                  name={id}
                  label={startCase(id)}
                  value={values[id]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched[id] && Boolean(errors[id])}
                  helperText={touched[id] && errors[id]}
                  disabled={isSubmitting}
                />
              </Grid>
            ))}
          </Grid>

          {/* Transaction Info */}
          <Typography variant="h6" gutterBottom mt={3}>
            Transaction Info
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} key={"merchant_reference_id"}>
              <TextField
                fullWidth
                size="small"
                id={"merchant_reference_id"}
                name={"merchant_reference_id"}
                label={startCase("merchant_reference_id")}
                value={values["merchant_reference_id"]}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched["merchant_reference_id"] &&
                  Boolean(errors["merchant_reference_id"])
                }
                helperText={
                  touched["merchant_reference_id"] &&
                  errors["merchant_reference_id"]
                }
                disabled={isSubmitting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Generate"
                        edge="end"
                        onClick={handleRandom}
                      >
                        <AutorenewIcon color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {["description", "currency", "amount"].map((id) => (
              <Grid item xs={12} md={6} key={id}>
                <TextField
                  fullWidth
                  size="small"
                  id={id}
                  name={id}
                  label={startCase(id)}
                  value={values[id]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched[id] && Boolean(errors[id])}
                  helperText={touched[id] && errors[id]}
                  disabled={isSubmitting}
                />
              </Grid>
            ))}
          </Grid>

          {/* Payer Info */}
          <Typography variant="h6" gutterBottom mt={3}>
            Payer Info
          </Typography>
          <Grid container spacing={2}>
            {["name", "email", "phone"].map((id) => (
              <Grid item xs={12} md={6} key={id}>
                <TextField
                  fullWidth
                  size="small"
                  id={id}
                  name={id}
                  label={startCase(id)}
                  value={values[id]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched[id] && Boolean(errors[id])}
                  helperText={touched[id] && errors[id]}
                  disabled={isSubmitting}
                />
              </Grid>
            ))}
          </Grid>

          {/* URL Info */}
          <Typography variant="h6" gutterBottom mt={3}>
            URL Info
          </Typography>
          <Grid container spacing={2}>
            {["success_url", "failure_url", "notification_url"].map((id) => (
              <Grid item xs={12} key={id}>
                <TextField
                  fullWidth
                  size="small"
                  id={id}
                  name={id}
                  label={startCase(id)}
                  value={values[id]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched[id] && Boolean(errors[id])}
                  helperText={touched[id] && errors[id]}
                  disabled={isSubmitting}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="capture"
                    checked={values.capture}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                }
                label="Capture"
              />
            </Grid>
          </Grid>

          <Box mt={3} textAlign="center">
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              loading={isSubmitting}
              sx={{ ...styles.customButton }}
            >
              Submit
            </LoadingButton>
          </Box>
        </form>
      </Paper>
    </Container>
  );
}
